import { Controller } from "@hotwired/stimulus";
import mapboxgl from "mapbox-gl";

export default class extends Controller {
  static values = {
    apiEndpoint: String,
    styleUrl: String,
  };

  static targets = ["container", "loader"];

  connect() {
    if (!mapboxgl.supported()) {
      console.warn("WebGL not supported");
      return;
    }

    this.markerTemplate = this.element.querySelector(
      '[data-map-template="marker"]'
    );
    this.popupTemplate = this.element.querySelector(
      '[data-map-template="popup"]'
    );

    this.loadDataAndInitMap();
  }

  async loadDataAndInitMap() {
    try {
      const response = await fetch(this.apiEndpointValue);
      const geojson = await response.json();
      const features = geojson.features ?? [];

      if (features.length === 0) {
        console.warn("No features found");
        this.hideLoader();
        return;
      }

      this.initMapWithData(features);
    } catch (err) {
      console.error("Error loading map data:", err);
      this.hideLoader();
    }
  }

  initMapWithData(features) {
    mapboxgl.accessToken =
      "pk.eyJ1IjoicnNiYXVtYW5uIiwiYSI6ImNqdzg5aWxkYzF1azI0OW5uaWVmazhleXUifQ.XAm1dRGmXuRAMSQm0TJKXg";

    const style = this.hasStyleUrlValue
      ? this.styleUrlValue
      : this.defaultStyle();

    const map = new mapboxgl.Map({
      container: this.hasContainerTarget ? this.containerTarget : this.element,
      style: style,
      center: [-70.65, -33.45],
      zoom: 9,
    });

    this.map = map;
    const bounds = new mapboxgl.LngLatBounds();

    features.forEach((feature) => {
      const { coordinates } = feature.geometry;
      const props = feature.properties;

      const markerFragment = this.renderTemplate(this.markerTemplate, props);
      const markerEl = markerFragment.firstElementChild || this.defaultMarker();

      const popupHtml = this.popupTemplate
        ? this.renderTemplate(this.popupTemplate, props).firstElementChild
            ?.outerHTML
        : null;

      const marker = new mapboxgl.Marker(markerEl).setLngLat(coordinates);

      if (popupHtml) {
        marker.setPopup(
          new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(
            popupHtml
          )
        );
      }

      marker.addTo(map);
      bounds.extend(coordinates);
    });

    map.on("load", () => {
      map.fitBounds(bounds, { padding: 40, maxZoom: 10 });
      this.hideLoader();
    });
  }

  hideLoader() {
    if (this.hasLoaderTarget) {
      this.loaderTarget.classList.add("opacity-0", "pointer-events-none");
      setTimeout(() => this.loaderTarget.remove(), 500);
    }
  }

  defaultStyle() {
    return "mapbox://styles/rsbaumann/ck9ktelcs23ss1jqm8jn561yx";
  }

  renderTemplate(template, props) {
    if (!template) return document.createDocumentFragment();
    const clone = template.content.cloneNode(true);

    clone.querySelectorAll("[data-map-if]").forEach((el) => {
      const key = el.getAttribute("data-map-if");
      if (!props[key]) el.remove();
    });

    clone.querySelectorAll("[data-map-unless]").forEach((el) => {
      const key = el.getAttribute("data-map-unless");
      if (props[key]) el.remove();
    });

    clone.querySelectorAll("[data-map-field]").forEach((el) => {
      const key = el.getAttribute("data-map-field");
      let value = props[key] ?? "";

      // Detecta ISO 8601 (YYYY-MM-DDTHH:mm:ss...) y formatea bonito
      if (
        typeof value === "string" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)
      ) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            value = date.toLocaleString("es-CL", {
              dateStyle: "medium",
              timeStyle: "short",
            });
          }
        } catch (_) {
          // fall back al valor original
        }
      }

      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
        el.value = value;
      } else {
        el.textContent = value;
      }
    });

    return clone;
  }

  defaultMarker() {
    const el = document.createElement("div");
    el.className = "w-4 h-4 rounded-full ring-2 bg-yellow-400";
    return el;
  }
}
